import { ContentType } from "JS/Models/Media";

export type DisclosurePlacement = "start" | "end";

export type DisclosureFailureReason =
    | "signedUrlRequestFailed"
    | "signedUrlInvalid"
    | "httpError"
    | "networkUnavailable"
    | "timeout"
    | "emptyFile"
    | "sizeMismatch"
    | "storageFailed"
    | "cancelled"
    | "unknown"
    | "offlineNotCached";

export type DisclosurePlayReason = DisclosureFailureReason;

export type DisclosureDownloadReason = DisclosureFailureReason;

export interface DisclosurePlayContext {
    skuId?: string;
    nId?: string;
    contentType?: ContentType;
}

export interface DisclosurePlayEvent extends DisclosurePlayContext {
    disclosureId: string;
    placement: DisclosurePlacement;
    reason?: DisclosureFailureReason;
}

export interface DisclosureClip {
    bucket_name: string;
    postfix: string;
    file_name: string;
    url?: string;
}

export interface Disclosure {
    start?: DisclosureClip;
    end?: DisclosureClip;
}

export interface DisclosureCatalogueEntry {
    start: DisclosureClip[];
    end: DisclosureClip[];
}

export type DisclosureCatalogue = Partial<
    Record<ContentType, DisclosureCatalogueEntry>
>;

export interface DisclosureUrls {
    startUrl?: string;
    endUrl?: string;
}

export const hasClip = (clip?: DisclosureClip) => !!clip?.file_name;

export const requiresDisclosure = (disclosure?: Disclosure) =>
    hasClip(disclosure?.start) || hasClip(disclosure?.end);

export const clipId = (clip?: DisclosureClip) =>
    hasClip(clip) ? clip.file_name : "";

export const catalogueClips = (catalogue?: DisclosureCatalogue) => {
    if (!catalogue) return [];

    const clips = Object.keys(catalogue).flatMap((type) => [
        ...(catalogue[type]?.start ?? []),
        ...(catalogue[type]?.end ?? []),
    ]);

    const seen = new Set<string>();
    return clips.filter((clip) => {
        const id = clipId(clip);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
    });
};

export const disclosureFingerprint = (catalogue?: DisclosureCatalogue) =>
    catalogueClips(catalogue).map(clipId).sort().join(";");

export interface DisclosureClipDescription {
    contentType?: ContentType;
    placement?: DisclosurePlacement;
}

export const describeClip = (
    clip?: DisclosureClip,
    catalogue?: DisclosureCatalogue,
): DisclosureClipDescription => {
    const id = clipId(clip);
    if (!id || !catalogue) return {};

    for (const type of Object.keys(catalogue) as ContentType[]) {
        const entry = catalogue[type];
        const placements: DisclosurePlacement[] = ["start", "end"];

        for (const placement of placements) {
            const found = entry?.[placement]?.some(
                (candidate) => clipId(candidate) === id,
            );
            if (found) return { contentType: type, placement };
        }
    }

    return {};
};
